import { ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { DomainService } from './services/white-label/domain.service';
import { AuthenticationService } from './authentication/authentication.service';
import { AuthGuard } from './guards/auth.guard';
import { RedirectGuard } from './guards/redirect.guard';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { UserService } from './services/users/user.service';
import { ThemeService } from './services/white-label/theme.service';
import { WhiteLabelService } from './services/white-label/white-label.service';
import { WindowRefService } from './services/helpers/window-ref.service';
import { DocumentRefService } from './services/helpers/document-ref.service';
import { DomainAdapter } from './services/white-label/domain.adapter';
import { LoadingService } from './services/loading.service';
import { NotificationsService } from './services/notifications.service';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { CountryService } from './services/country/country.service';
import { CountryAdapter } from './services/country/country.adapter';
import { CountriesStoreService } from './stores/countries-store.service';
import { CountriesResolverService } from './resolvers/countries-resolver.service';
import { LoginService } from './authentication/login.service';
import { CompanyAccountAdapter } from './authentication/company-account.adapter';
import { AuthenticatedUserAdapter } from './authentication/authenticated-user.adapter';
import { EncryptingService } from './services/encrypting.service';
import { SelectedAccountStore } from './stores/selected-account-store.service';
import { ServiceAccountGuard } from './guards/service-account.guard';
import { AppInitializerService } from './services/app-initializer.service';
import { HttpClientSkippingInterceptorsService } from './services/http-client-skipping-interceptors.service';
import { CompanyService } from './services/company.service';
import { AssetAdapter } from './services/adapters/asset.adapter';
import { ModelAdapter } from './services/adapters/model.adapter';
import { TagAdapter } from './services/adapters/tag.adapter';
import { CompanyAssetsStatsAdapter } from './services/adapters/company-assets-stats.adapter';
import { NavigatorRefService } from './services/helpers/navigator-ref.service';
import { AssetGroupService } from './services/asset-groups.service';
import { AssetGroupAdapter } from './services/adapters/asset-group.adapter';
import { FeatureFlagService } from './services/feature-flag.service';
import { FeatureFlagGuard } from './guards/feature-flag.guard';
import { MenuResolverService } from './resolvers/menu-resolver.service';
import { UserPreferencesStore } from './stores/user-preferences-store.service';
import { UserPreferencesAdapter } from './services/adapters/user-preferences.adapter';
import { InternationalizationService } from '@core/services/internationalization.service';
import { AdminUserGuard } from './guards/admin-user.guard';
import { ShiftAdapter } from '@core/services/adapters/shift.adapter';
import { TimezoneService } from '@core/services/timezone.service';
import { HelpService } from '@core/services/help.service';
import { TimezoneAdapter } from '@core/services/adapters/timezone.adapter';
import { TimezoneHelper } from '@core/services/helpers/timezone.helper';
import { TimezonesStoreService } from '@core/stores/timezones-store.service';
import { TimezonesResolverService } from '@core/resolvers/timezones-resolver.service';
import { AssetImagesStoreService } from '@core/stores/asset-images-store.service';
import { FullscreenService } from '@core/services/helpers/fullscreen.service';
import { DashboardGuard } from '@core/guards/dashboard.guard';
import { AssetService } from '@core/services/asset.service';
import { AssetStatusAdapter } from '@core/services/adapters/asset-status.adapter';
import { NavigationService } from '@core/services/navigation.service';
import { DomainGuard } from '@core/guards/domain.guard';
import { UserNotificationService } from '@core/services/users/user-notification.service';
import { UserReportNotificationSubscriptionAdapter } from '@core/services/adapters/user-report-notification-subscription.adapter';
import { UserReportSubscriptionsAdapter } from '@core/services/adapters/user-report-subscriptions.adapter';
import { AssetStoreService } from './stores/asset-store.service';

@NgModule({
    imports: [MatSnackBarModule],
    providers: [provideHttpClient(withInterceptorsFromDi())],
})
export class CoreModule {
    constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
        if (parentModule) {
            throw new Error('CoreModule is already loaded. Import it in the AppModule only');
        }
    }

    static forRoot(): ModuleWithProviders<CoreModule> {
        return {
            ngModule: CoreModule,
            providers: [
                AdminUserGuard,
                AppInitializerService,
                AssetAdapter,
                AssetGroupAdapter,
                AssetGroupService,
                AssetImagesStoreService,
                AssetService,
                AssetStatusAdapter,
                AssetStoreService,
                AuthGuard,
                AuthInterceptor,
                AuthenticatedUserAdapter,
                AuthenticationService,
                CompanyAccountAdapter,
                CompanyAssetsStatsAdapter,
                CompanyService,
                CountriesResolverService,
                CountriesStoreService,
                CountryAdapter,
                CountryService,
                DashboardGuard,
                DocumentRefService,
                DomainAdapter,
                DomainGuard,
                DomainService,
                EncryptingService,
                ErrorInterceptor,
                FeatureFlagGuard,
                FeatureFlagService,
                FullscreenService,
                HelpService,
                HttpClientSkippingInterceptorsService,
                InternationalizationService,
                LoadingService,
                LoginService,
                MenuResolverService,
                ModelAdapter,
                NavigationService,
                NavigatorRefService,
                NotificationsService,
                RedirectGuard,
                SelectedAccountStore,
                ServiceAccountGuard,
                ShiftAdapter,
                TagAdapter,
                ThemeService,
                TimezoneAdapter,
                TimezoneHelper,
                TimezoneService,
                TimezonesStoreService,
                TimezonesResolverService,
                UserNotificationService,
                UserPreferencesAdapter,
                UserPreferencesStore,
                UserReportNotificationSubscriptionAdapter,
                UserReportSubscriptionsAdapter,
                UserService,
                WhiteLabelService,
                WindowRefService,
            ],
        };
    }
}
